/**
 * CDB-TQS API — Excel Import and Excel Enrichment Profiles
 */

import { logApiCall, logApiResponse, logError } from "@/lib/logger";
import { CDB_TQS_API_BASE_URL, fetchWithLogging, getAuthHeaders } from "./http";
// Enrichment profiles live with their router source (cdb_tqs#1578).
import { routerFetch } from "./router";

/** `?knowledge_area=` for the by-id mapping calls, which route by the area's client. */
function mappingAreaParams(knowledgeArea?: string): Record<string, string> | undefined {
  return knowledgeArea ? { knowledge_area: knowledgeArea } : undefined
}

export const excelApi = {
  /**
   * Get Excel preview (sheet names, headers, sample rows)
   * @param file - Excel file
   * @param headerRow - Row index for headers (0-based)
   * @returns Preview response with sheets
   */
  async getExcelPreview(file: File, headerRow: number = 0): Promise<any> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('header_row', String(headerRow))

    const url = `${CDB_TQS_API_BASE_URL}/excel/preview`

    logApiCall('POST', url, { filename: file.name, headerRow })

    try {
      const authHeaders = await getAuthHeaders()

      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders,
        body: formData,
      })

      const data = response.headers.get('content-type')?.includes('application/json')
        ? await response.json()
        : await response.text()

      logApiResponse('POST', url, response.status, data)

      if (!response.ok) {
        throw new Error(`Excel preview failed: ${response.status} ${response.statusText}`)
      }

      return data
    } catch (error) {
      logError(error as Error, {
        method: 'POST',
        url,
        context: 'Excel preview failed',
      })
      throw error
    }
  },

  /**
   * List Excel mappings for a knowledge area
   * @param knowledgeArea - Optional knowledge area filter
   * @param activeOnly - Only return active mappings
   * @returns List of mappings
   */
  async listExcelMappings(knowledgeArea?: string, activeOnly: boolean = true): Promise<any> {
    const params = new URLSearchParams()
    if (knowledgeArea) params.append('knowledge_area', knowledgeArea)
    if (activeOnly !== undefined) params.append('active_only', String(activeOnly))

    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/excel/mappings?${params}`,
      { method: 'GET' }
    )

    return response.data
  },

  /**
   * Get a single Excel mapping by ID
   * @param mappingId - Mapping UUID
   * @param knowledgeArea - Knowledge area of the mapping. A mapping lives in the
   *   database of its area's client (cdb_tqs#1577); without the area only the
   *   global mappings and the caller's own client's are searched.
   * @returns Mapping details
   */
  async getExcelMapping(mappingId: string, knowledgeArea?: string): Promise<any> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/excel/mappings/${mappingId}`,
      { method: 'GET', params: mappingAreaParams(knowledgeArea) }
    )

    return response.data
  },

  /**
   * Create a new Excel mapping
   * @param data - Mapping configuration
   * @returns Created mapping
   */
  async createExcelMapping(data: any): Promise<any> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/excel/mappings`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )

    return response.data
  },

  /**
   * Update an Excel mapping
   * @param mappingId - Mapping UUID
   * @param data - Partial mapping data
   * @returns Updated mapping
   */
  async updateExcelMapping(mappingId: string, data: any): Promise<any> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/excel/mappings/${mappingId}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      }
    )

    return response.data
  },

  /**
   * Delete an Excel mapping
   * @param mappingId - Mapping UUID
   * @param knowledgeArea - Knowledge area of the mapping (see getExcelMapping)
   */
  async deleteExcelMapping(mappingId: string, knowledgeArea?: string): Promise<void> {
    await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/excel/mappings/${mappingId}`,
      { method: 'DELETE', params: mappingAreaParams(knowledgeArea) }
    )
  },

  /**
   * Process an Excel file
   * @param file - Excel file
   * @param processRequest - Processing configuration
   * @returns Import response with import_id and job_id
   */
  async processExcel(file: File, processRequest: any): Promise<any> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('knowledge_area', processRequest.knowledge_area)
    formData.append('client_id', processRequest.client_id)

    if (processRequest.mapping_id) {
      formData.append('mapping_id', processRequest.mapping_id)
    }

    if (processRequest.mapping_config) {
      formData.append('mapping_config_json', JSON.stringify(processRequest.mapping_config))
    }

    if (processRequest.save_mapping) {
      formData.append('save_mapping', 'true')
    }

    if (processRequest.mapping_name) {
      formData.append('mapping_name', processRequest.mapping_name)
    }

    if (processRequest.mapping_description) {
      formData.append('mapping_description', processRequest.mapping_description)
    }

    if (processRequest.generate_embeddings !== undefined) {
      formData.append('generate_embeddings', String(processRequest.generate_embeddings))
    }

    if (processRequest.upload_description) {
      formData.append('upload_description', processRequest.upload_description)
    }

    const url = `${CDB_TQS_API_BASE_URL}/excel/process`

    logApiCall('POST', url, {
      filename: file.name,
      knowledgeArea: processRequest.knowledge_area,
      mappingId: processRequest.mapping_id,
    })

    try {
      const authHeaders = await getAuthHeaders()

      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders,
        body: formData,
      })

      const data = response.headers.get('content-type')?.includes('application/json')
        ? await response.json()
        : await response.text()

      logApiResponse('POST', url, response.status, data)

      if (!response.ok) {
        throw new Error(`Excel processing failed: ${response.status} ${response.statusText}`)
      }

      return data
    } catch (error) {
      logError(error as Error, {
        method: 'POST',
        url,
        context: 'Excel processing failed',
      })
      throw error
    }
  },

  /**
   * Get Excel import status
   * @param importId - Import UUID
   * @returns Import progress and statistics
   */
  async getExcelImportStatus(importId: string): Promise<any> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/excel/imports/${importId}/status`,
      { method: 'GET' }
    )

    return response.data
  },

  // -------------------------------------------------------------------------
  // Excel Enrichment Profiles (#448)
  // -------------------------------------------------------------------------

  async listExcelEnrichmentProfiles(opts?: {
    sourceId?: string
  }): Promise<import('@/types/toolbox').ExcelEnrichmentProfile[]> {
    const params: Record<string, string> = {}
    if (opts?.sourceId) params.source_id = opts.sourceId
    const response = await routerFetch(
      `${CDB_TQS_API_BASE_URL}/excel-enrichment-profiles`,
      { method: 'GET', params },
    )
    return response.data
  },

  async getExcelEnrichmentProfile(
    id: string,
  ): Promise<import('@/types/toolbox').ExcelEnrichmentProfile> {
    const response = await routerFetch(
      `${CDB_TQS_API_BASE_URL}/excel-enrichment-profiles/${id}`,
      { method: 'GET' },
    )
    return response.data
  },

  async createExcelEnrichmentProfile(
    data: import('@/types/toolbox').ExcelEnrichmentProfileCreate,
  ): Promise<import('@/types/toolbox').ExcelEnrichmentProfile> {
    const response = await routerFetch(
      `${CDB_TQS_API_BASE_URL}/excel-enrichment-profiles`,
      { method: 'POST', body: JSON.stringify(data) },
    )
    return response.data
  },

  async updateExcelEnrichmentProfile(
    id: string,
    patch: import('@/types/toolbox').ExcelEnrichmentProfileUpdate,
  ): Promise<import('@/types/toolbox').ExcelEnrichmentProfile> {
    const response = await routerFetch(
      `${CDB_TQS_API_BASE_URL}/excel-enrichment-profiles/${id}`,
      { method: 'PATCH', body: JSON.stringify(patch) },
    )
    return response.data
  },

  async deleteExcelEnrichmentProfile(id: string): Promise<void> {
    await routerFetch(
      `${CDB_TQS_API_BASE_URL}/excel-enrichment-profiles/${id}`,
      { method: 'DELETE' },
    )
  },
};
